import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Badge, Spinner, Modal } from '../../components/UI';
import { attendanceAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineDownload,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineArrowRight,
  HiOutlineUserGroup,
  HiOutlineChartBar,
  HiOutlineLogin,
  HiOutlineDesktopComputer,
  HiOutlinePhone,
  HiOutlineChatAlt2,
  HiOutlineMail,
  HiOutlineClipboardCheck,
  HiOutlinePencilAlt
} from 'react-icons/hi';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';
import { formatLabel } from '../../utils/formatLabel';

const COLORS = {
  active: '#E7000B',
  completed: '#10B981',
  notCheckedIn: '#F59E0B'
};

// Build compact pagination with ellipsis
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    primary: 'bg-primary-600',
    success: 'bg-green-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500'
  };

  return (
    <Card className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color] || 'bg-primary-600'}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </Card>
  );
};

const Attendance = () => {
  const [reportTab, setReportTab] = useState('checkin'); // 'checkin' or 'login'
  const [filterMode, setFilterMode] = useState('day'); // 'day' or 'month'
  // Local yyyy-MM-dd — toISOString() is UTC and shifts the date back a day
  // for IST before 05:30 (and the month range by a day at both ends).
  const [dateFilter, setDateFilter] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [monthFilter, setMonthFilter] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [userFilter, setUserFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Login Duration Tracking
  const [loginPage, setLoginPage] = useState(1);
  const [loginRecord, setLoginRecord] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Calculate date range based on filter mode
  const getDateRange = () => {
    if (filterMode === 'day') {
      return { startDate: dateFilter, endDate: dateFilter };
    } else {
      // Month mode - calculate first and last day of month
      const [year, month] = monthFilter.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of month
      return {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
    }
  };

  const dateRange = getDateRange();

  // Fetch users for filter
  const { data: usersData } = useQuery({
    queryKey: ['usersForFilter'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ limit: 100 });
      return response.data?.data || [];
    },
  });

  // Fetch attendance records
  const { data: attendanceData, isLoading, refetch } = useQuery({
    queryKey: ['attendance', filterMode, filterMode === 'day' ? dateFilter : monthFilter, userFilter, page, pageSize],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      if (filterMode === 'day') {
        params.date = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      if (userFilter) params.userId = userFilter;
      const response = await attendanceAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['attendanceStats', filterMode, filterMode === 'day' ? dateFilter : monthFilter],
    queryFn: async () => {
      const params = {};
      if (filterMode === 'day') {
        params.date = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      const response = await attendanceAPI.getStats(params);
      return response.data?.data || {};
    },
  });

  const records = attendanceData?.data || [];
  const pagination = attendanceData?.pagination || {};
  const users = usersData || [];

  // ========================================
  // LOGIN DURATION TRACKING (Reports)
  // ========================================
  const { data: loginData, isLoading: loginLoading, refetch: refetchLogin } = useQuery({
    queryKey: ['loginDuration', filterMode, filterMode === 'day' ? dateFilter : monthFilter, userFilter, loginPage, pageSize],
    queryFn: async () => {
      const params = { page: loginPage, limit: pageSize };
      if (filterMode === 'day') {
        params.startDate = dateFilter;
        params.endDate = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      if (userFilter) params.userId = userFilter;
      const response = await attendanceAPI.getLoginDuration(params);
      return response.data || {};
    },
    enabled: reportTab === 'login',
  });

  // Individual login/logout sessions for the selected agent (detail modal)
  const { data: loginSessions, isLoading: loginSessionsLoading } = useQuery({
    queryKey: ['loginSessions', loginRecord?.userId, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      const response = await attendanceAPI.getLoginDurationSessions({
        userId: loginRecord.userId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return response.data?.data || [];
    },
    enabled: !!loginRecord,
  });

  const loginRecords = loginData?.data || [];
  const loginSummary = loginData?.summary || {};
  const loginPagination = loginData?.pagination || {};

  // ========================================
  // EMPLOYEE ACTIVITY TIMELINE
  // Chronological history: calls, follow-ups, messages, CRM actions
  // ========================================
  const [activityRecord, setActivityRecord] = useState(null); // { userId, userName, date }

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['activityTimeline', activityRecord?.userId, activityRecord?.date],
    queryFn: async () => {
      const response = await attendanceAPI.getActivityTimeline({
        userId: activityRecord.userId,
        date: activityRecord.date,
        limit: 200
      });
      return response.data || {};
    },
    enabled: !!activityRecord,
  });

  const openActivityTimeline = (record) => {
    setActivityRecord({
      userId: record.userId?._id,
      userName: `${record.userId?.firstName} ${record.userId?.lastName}`,
      date: format(new Date(record.date), 'yyyy-MM-dd')
    });
  };

  // Timeline icon + color per activity type
  const activityStyle = {
    call:     { icon: HiOutlinePhone,          bg: 'bg-blue-100',    text: 'text-blue-600' },
    followup: { icon: HiOutlineClipboardCheck, bg: 'bg-purple-100',  text: 'text-purple-600' },
    message:  { icon: HiOutlineChatAlt2,       bg: 'bg-green-100',   text: 'text-green-600' },
    crm:      { icon: HiOutlinePencilAlt,      bg: 'bg-amber-100',   text: 'text-amber-600' }
  };

  const getActivityDisplay = (item) => {
    const style = activityStyle[item.activityType] || activityStyle.crm;
    let Icon = style.icon;
    if (item.activityType === 'message' && item.subType === 'email') Icon = HiOutlineMail;
    return { Icon, bg: style.bg, text: style.text };
  };

  // Pie chart data
  const pieData = [
    { name: 'Active Sessions', value: stats?.activeSessions || 0, color: COLORS.active },
    { name: 'Completed Sessions', value: stats?.completedSessions || 0, color: COLORS.completed },
  ].filter(item => item.value > 0);

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    return format(new Date(timeStr), 'hh:mm a');
  };

  const formatDuration = (hours) => {
    if (!hours) return '0 H 0 M';
    // Convert hours to total minutes (backend sends hours as decimal, e.g., 0.85 hours = 51 minutes)
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h} H ${m} M`;
  };

  // Login durations come back from the backend in seconds
  const formatSeconds = (seconds) => {
    if (!seconds || seconds <= 0) return '0h 0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const formatDateTime = (timeStr) => {
    if (!timeStr) return '--';
    return format(new Date(timeStr), 'MMM d, hh:mm a');
  };

  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleLoginRowClick = (record) => {
    setLoginRecord(record);
    setIsLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
    setLoginRecord(null);
  };

  // Download helper shared by both exports
  const downloadCSV = (csvRows, fileName) => {
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded successfully');
  };

  const handleLoginExport = async () => {
    try {
      toast.loading('Generating report...');
      const params = { page: 1, limit: 1000 };
      if (filterMode === 'day') {
        params.startDate = dateFilter;
        params.endDate = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      if (userFilter) params.userId = userFilter;

      const response = await attendanceAPI.getLoginDuration(params);
      toast.dismiss();

      const rows = response.data?.data || [];
      if (rows.length === 0) {
        toast.error('No data to export');
        return;
      }

      const csvRows = [];
      csvRows.push('=== LOGIN DURATION EXPORT ===');
      csvRows.push('');
      csvRows.push(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.push(`Date Range: ${filterMode === 'day' ? dateFilter : `${dateRange.startDate} to ${dateRange.endDate}`}`);
      csvRows.push(`Total Agents: ${rows.length}`);
      csvRows.push('');

      csvRows.push([
        'Agent Name',
        'Email',
        'Role',
        'Department',
        'Login Sessions',
        'Total Login Duration',
        'Avg Session Duration',
        'Last Login',
        'Status'
      ].join(','));

      rows.forEach(r => {
        csvRows.push([
          `"${r.name || ''}"`,
          `"${r.email || ''}"`,
          `"${r.role || ''}"`,
          `"${r.department || ''}"`,
          `"${r.loginSessions ?? 0}"`,
          `"${formatSeconds(r.totalSeconds)}"`,
          `"${formatSeconds(r.avgSeconds)}"`,
          `"${r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString() : ''}"`,
          `"${r.isOnline ? 'Online' : 'Offline'}"`
        ].join(','));
      });

      const fileName = filterMode === 'day' ? `login-duration-${dateFilter}.csv` : `login-duration-${monthFilter}.csv`;
      downloadCSV(csvRows, fileName);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
      console.error('Login duration export error:', error);
    }
  };

  const handleExport = async () => {
    try {
      toast.loading('Generating report...');
      const params = { startDate: dateRange.startDate, endDate: dateRange.endDate };
      if (userFilter) params.userId = userFilter;

      const response = await attendanceAPI.exportReport(params);
      toast.dismiss();

      if (response.data?.success && response.data?.data) {
        const records = response.data.data;
        if (records.length === 0) {
          toast.error('No data to export');
          return;
        }

        // Build well-structured CSV - backend already flattens sessions
        const csvRows = [];

        // Header
        csvRows.push('=== ATTENDANCE EXPORT ===');
        csvRows.push('');
        csvRows.push(`Export Date: ${new Date().toLocaleString()}`);
        csvRows.push(`Date Range: ${filterMode === 'day' ? dateFilter : `${monthFilter}`}`);
        csvRows.push(`Total Sessions: ${records.length}`);
        csvRows.push('');

        // Column headers - matching backend response
        csvRows.push([
          'Date',
          'Employee Name',
          'Email',
          'Department',
          'Session #',
          'Check In Time',
          'Check Out Time',
          'Working Hours',
          'Status'
        ].join(','));

        // Data rows - backend returns flattened records
        records.forEach(record => {
          csvRows.push([
            `"${record.Date || ''}"`,
            `"${record['Employee Name'] || ''}"`,
            `"${record.Email || ''}"`,
            `"${record.Department || ''}"`,
            `"${record['Session #'] || 1}"`,
            `"${record['Check In'] || ''}"`,
            `"${record['Check Out'] || ''}"`,
            `"${record['Working Hours'] || 'N/A'}"`,
            `"${formatLabel(record['Day Status'] || record.Status || '')}"`
          ].join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = filterMode === 'day' ? `attendance-${dateFilter}.csv` : `attendance-${monthFilter}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded successfully');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
      console.error('Export error:', error);
    }
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Attendance Management</h1>
            <p className="text-gray-500 text-sm mt-0.5 sm:mt-1">Track telecaller check-in/out sessions and system login time</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Report Tabs */}
            <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden self-start">
              <button
                type="button"
                onClick={() => { setReportTab('checkin'); setPage(1); setLoginPage(1); }}
                className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                  reportTab === 'checkin'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Check-In Report
              </button>
              {/* Login Duration tab commented out (hidden, not removed).
                  To re-enable, uncomment the button below.
              <button
                type="button"
                onClick={() => { setReportTab('login'); setLoginPage(1); }}
                className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                  reportTab === 'login'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Login Duration
              </button>
              */}
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineRefresh className="w-4 h-4" />}
              onClick={() => (reportTab === 'login' ? refetchLogin() : refetch())}
            >
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={reportTab === 'login' ? handleLoginExport : handleExport}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {reportTab === 'checkin' && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            title="Total Telecallers"
            value={stats?.totalTelecallers ?? stats?.totalRecords ?? 0}
            icon={<HiOutlineUser className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="primary"
          />
          <StatCard
            title="Currently Active"
            value={stats?.activeUsers || 0}
            icon={<HiOutlineCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="success"
          />
          <StatCard
            title="Total Sessions"
            value={stats?.totalSessions || 0}
            icon={<HiOutlineClock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="info"
          />
          <StatCard
            title="Total Hours"
            value={stats?.totalWorkingHoursFormatted || '0h 0m'}
            icon={<HiOutlineCalendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="warning"
          />
        </div>
        )}

        {/* Charts Row */}
        {reportTab === 'checkin' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {/* Pie Chart */}
          <Card padding="p-3 sm:p-4 lg:p-6" className="lg:col-span-1">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Session Status</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={160} className="sm:h-[200px]">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-40 sm:h-48 text-gray-400 text-sm">
                No data available
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mt-3 sm:mt-4">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Active Telecallers List */}
          <Card padding="p-3 sm:p-4 lg:p-6" className="lg:col-span-2">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Currently Active Telecallers</h3>
            {stats?.activeTelecallers?.length > 0 ? (
              <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                {stats.activeTelecallers.map((telecaller) => (
                  <div key={telecaller.userId} className="flex items-center justify-between p-2.5 sm:p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-green-700 font-medium text-sm">
                          {telecaller.name?.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{telecaller.name}</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{telecaller.department}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <Badge variant="success" size="sm">Active</Badge>
                      <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">
                        Since {formatTime(telecaller.checkInTime)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 sm:h-48 text-gray-400 text-sm">
                No telecallers currently active
              </div>
            )}
          </Card>
        </div>
        )}

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Filter Mode Toggle */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">View By</label>
              <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setFilterMode('day'); setPage(1); setLoginPage(1); }}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                    filterMode === 'day'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Day
                </button>
                <button
                  type="button"
                  onClick={() => { setFilterMode('month'); setPage(1); setLoginPage(1); }}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                    filterMode === 'month'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Month
                </button>
              </div>
            </div>
            {/* Date/Month Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {filterMode === 'day' ? 'Date' : 'Month'}
              </label>
              {filterMode === 'day' ? (
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => { setDateFilter(e.target.value); setPage(1); setLoginPage(1); }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none text-sm"
                />
              ) : (
                <input
                  type="month"
                  value={monthFilter}
                  onChange={(e) => { setMonthFilter(e.target.value); setPage(1); setLoginPage(1); }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none text-sm"
                />
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {reportTab === 'login' ? 'Agent' : 'Telecaller'}
              </label>
              <select
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setPage(1); setLoginPage(1); }}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none bg-white text-sm"
              >
                <option value="">{reportTab === 'login' ? 'All Agents' : 'All Telecallers'}</option>
                {users
                  .filter(u => (reportTab === 'login' ? true : u.role === 'telecaller'))
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} {reportTab === 'login' ? `(${formatLabel(user.role)})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Attendance Table */}
        {reportTab === 'checkin' && (isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : records.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center py-8 sm:py-12">
              <HiOutlineClock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900">No attendance records</h3>
              <p className="text-gray-500 text-sm mt-1 text-center px-4">Attendance records for the selected date will appear here</p>
            </div>
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Telecaller</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Check In</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Check Out</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Sessions</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Total Hours</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((record) => {
                    const sessions = record.sessions || [];
                    const isActive = sessions.some(s => s.status === 'active');

                    return (
                      <tr
                        key={record._id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(record)}
                      >
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <div className="flex items-center gap-2 lg:gap-3">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {record.userId?.firstName?.charAt(0)}{record.userId?.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {record.userId?.firstName} {record.userId?.lastName}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-500">{record.userId?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4 text-gray-600 text-sm">
                          {format(new Date(record.date), 'MMM d, yyyy')}
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4 text-gray-600 text-sm">
                          {formatTime(record.checkInTime)}
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4 text-sm">
                          {isActive ? (
                            <span className="text-green-600 font-medium">Active</span>
                          ) : (
                            <span className="text-gray-600">{formatTime(record.checkOutTime)}</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <span className="font-medium text-sm">{sessions.length}</span>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <span className="font-medium text-primary-600 text-sm">
                            {formatDuration(record.totalWorkingHours || 0)}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <Badge variant={isActive ? 'success' : 'default'} size="sm">
                            {isActive ? 'Active' : 'Completed'}
                          </Badge>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <button
                            type="button"
                            title="View Activity Timeline"
                            onClick={(e) => { e.stopPropagation(); openActivityTimeline(record); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
                          >
                            <HiOutlineClock className="w-4 h-4" />
                            Timeline
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {records.map((record) => {
                const sessions = record.sessions || [];
                const isActive = sessions.some(s => s.status === 'active');

                return (
                  <div
                    key={record._id}
                    className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(record)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-medium text-sm">
                            {record.userId?.firstName?.charAt(0)}{record.userId?.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {record.userId?.firstName} {record.userId?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(record.date), 'MMM d, yyyy')} • {sessions.length} sessions
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            In: {formatTime(record.checkInTime)} • Out:{' '}
                            {isActive ? (
                              <span className="text-green-600 font-medium">Active</span>
                            ) : (
                              formatTime(record.checkOutTime)
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="font-medium text-primary-600 text-sm">
                          {formatDuration(record.totalWorkingHours || 0)}
                        </span>
                        <Badge variant={isActive ? 'success' : 'default'} size="sm">
                          {isActive ? 'Active' : 'Done'}
                        </Badge>
                        <button
                          type="button"
                          title="View Activity Timeline"
                          onClick={(e) => { e.stopPropagation(); openActivityTimeline(record); }}
                          className="p-1.5 rounded-lg text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
                        >
                          <HiOutlineClock className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
                  <span>
                    Showing {((pagination.page - 1) * pageSize) + 1}–{Math.min(pagination.page * pageSize, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="att-rows-per-page" className="text-xs sm:text-sm text-gray-500">Rows per page:</label>
                    <select
                      id="att-rows-per-page"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      className="px-2 py-1 border border-gray-200 rounded-md text-sm bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                {pagination.totalPages > 1 && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </Button>
                    {buildPageList(page, pagination.totalPages).map((item, idx) =>
                      item === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-400 select-none">…</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}

        {/* ========================================
            LOGIN DURATION TRACKING VIEW
        ======================================== */}
        {reportTab === 'login' && (
          <>
            {/* Login Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard
                title="Agents Logged In"
                value={loginSummary.agents || 0}
                icon={<HiOutlineUserGroup className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                color="primary"
              />
              <StatCard
                title="Online Now"
                value={loginSummary.onlineNow || 0}
                icon={<HiOutlineChartBar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                color="success"
              />
              <StatCard
                title="Total Login Time"
                value={formatSeconds(loginSummary.totalSeconds)}
                icon={<HiOutlineClock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                color="info"
              />
              <StatCard
                title="Avg Session"
                value={formatSeconds(loginSummary.avgSeconds)}
                icon={<HiOutlineLogin className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                color="warning"
              />
            </div>

            {/* Login Duration Table */}
            {loginLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : loginRecords.length === 0 ? (
              <Card>
                <div className="flex flex-col items-center py-8 sm:py-12">
                  <HiOutlineLogin className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">No login sessions</h3>
                  <p className="text-gray-500 text-sm mt-1 text-center px-4">Agent login activity for the selected period will appear here</p>
                </div>
              </Card>
            ) : (
              <Card padding="p-0">
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Agent</th>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Login Sessions</th>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Total Login Duration</th>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Avg Session</th>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Last Login</th>
                        <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {loginRecords.map((record) => (
                        <tr
                          key={record.userId}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleLoginRowClick(record)}
                        >
                          <td className="px-4 lg:px-6 py-3 lg:py-4">
                            <div className="flex items-center gap-2 lg:gap-3">
                              <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-primary-600 font-medium text-sm">
                                  {record.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{record.name}</p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">{record.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4">
                            <span className="font-medium text-sm">{record.loginSessions}</span>
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4">
                            <span className="font-medium text-primary-600 text-sm">
                              {formatSeconds(record.totalSeconds)}
                            </span>
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 text-gray-600 text-sm">
                            {formatSeconds(record.avgSeconds)}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 text-gray-600 text-sm">
                            {formatDateTime(record.lastLoginAt)}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4">
                            <Badge variant={record.isOnline ? 'success' : 'default'} size="sm">
                              {record.isOnline ? 'Online' : 'Offline'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-gray-100">
                  {loginRecords.map((record) => (
                    <div
                      key={record.userId}
                      className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleLoginRowClick(record)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-medium text-sm">
                              {record.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{record.name}</p>
                            <p className="text-xs text-gray-500">
                              {record.loginSessions} sessions • {formatDateTime(record.lastLoginAt)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="font-medium text-primary-600 text-sm">
                            {formatSeconds(record.totalSeconds)}
                          </span>
                          <Badge variant={record.isOnline ? 'success' : 'default'} size="sm">
                            {record.isOnline ? 'Online' : 'Offline'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {loginPagination.totalPages > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
                      <span>
                        Showing {((loginPagination.page - 1) * pageSize) + 1}–{Math.min(loginPagination.page * pageSize, loginPagination.total)} of {loginPagination.total}
                      </span>
                    </div>
                    {loginPagination.totalPages > 1 && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={loginPage === 1}
                          onClick={() => setLoginPage(p => p - 1)}
                        >
                          <span className="hidden sm:inline">Previous</span>
                          <span className="sm:hidden">Prev</span>
                        </Button>
                        {buildPageList(loginPage, loginPagination.totalPages).map((item, idx) =>
                          item === '...' ? (
                            <span key={`login-ellipsis-${idx}`} className="px-2 text-sm text-gray-400 select-none">…</span>
                          ) : (
                            <button
                              key={item}
                              type="button"
                              onClick={() => setLoginPage(item)}
                              className={
                                loginPage === item
                                  ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                                  : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                              }
                            >
                              {item}
                            </button>
                          )
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={loginPage === loginPagination.totalPages}
                          onClick={() => setLoginPage(p => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}
          </>
        )}

        {/* Session Details Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={selectedRecord ? `${selectedRecord.userId?.firstName} ${selectedRecord.userId?.lastName} - Session Details` : 'Session Details'}
          size="lg"
        >
          {selectedRecord && (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Date</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {format(new Date(selectedRecord.date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="bg-primary-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-primary-600">Total Hours</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary-700">
                    {formatDuration(selectedRecord.totalWorkingHours || 0)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Total Sessions</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {selectedRecord.sessions?.length || 0}
                  </p>
                </div>
              </div>

              {/* Sessions List */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Check-in / Check-out Times</h4>
                <div className="space-y-2 sm:space-y-3">
                  {selectedRecord.sessions?.map((session, index) => {
                    const isActive = session.status === 'active';
                    const sessionMinutes = session.workingHours || 0;

                    return (
                      <div
                        key={session._id || index}
                        className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 ${
                          isActive
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                          <span className="font-medium text-gray-900 text-sm">Session {index + 1}</span>
                          <Badge variant={isActive ? 'success' : 'default'} size="sm">
                            {isActive ? 'Active' : 'Completed'}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                          {/* Check In */}
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Check In</p>
                            <p className="text-sm sm:text-lg font-semibold text-gray-900">
                              {formatTime(session.checkIn?.time)}
                            </p>
                          </div>

                          {/* Arrow */}
                          <div className="flex items-center justify-center">
                            <HiOutlineArrowRight className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400" />
                          </div>

                          {/* Check Out */}
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Check Out</p>
                            <p className={`text-sm sm:text-lg font-semibold ${isActive ? 'text-green-600' : 'text-gray-900'}`}>
                              {session.checkOut?.time ? formatTime(session.checkOut.time) : 'Active'}
                            </p>
                          </div>

                          {/* Duration */}
                          <div className="flex-1 text-right">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Duration</p>
                            <p className="text-sm sm:text-lg font-semibold text-primary-600">
                              {formatDuration(sessionMinutes)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Employee Activity Timeline Modal */}
        <Modal
          isOpen={!!activityRecord}
          onClose={() => setActivityRecord(null)}
          title={activityRecord ? `${activityRecord.userName} - Activity Timeline` : 'Activity Timeline'}
          size="lg"
        >
          {/* Summary chips */}
          {activityData?.summary && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-blue-600">{activityData.summary.calls || 0}</p>
                <p className="text-xs text-gray-500">Calls</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-purple-600">{activityData.summary.followUps || 0}</p>
                <p className="text-xs text-gray-500">Follow-ups</p>
              </div>
              <div className="bg-green-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-green-600">{activityData.summary.messages || 0}</p>
                <p className="text-xs text-gray-500">Messages</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-amber-600">{activityData.summary.crmActions || 0}</p>
                <p className="text-xs text-gray-500">CRM</p>
              </div>
            </div>
          )}

          {/* Timeline */}
          {activityLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : !activityData?.data?.length ? (
            <div className="flex flex-col items-center py-10 text-gray-400">
              <HiOutlineClock className="w-10 h-10 mb-2" />
              <p className="text-sm">No activities found for this date</p>
            </div>
          ) : (
            <div className="max-h-[400px] sm:max-h-[480px] overflow-y-auto pr-1">
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-gray-200" aria-hidden="true" />
                <div className="space-y-4">
                  {activityData.data.map((item, idx) => {
                    const { Icon, bg, text } = getActivityDisplay(item);
                    return (
                      <div key={item._id || idx} className="relative flex items-start gap-3 pl-0">
                        {/* Dot / icon */}
                        <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white ${bg}`}>
                          <Icon className={`w-4 h-4 ${text}`} />
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {item.title}
                              {item.activityType === 'call' && item.duration > 0 && (
                                <span className="text-xs text-gray-400 ml-1.5 font-normal">
                                  ({Math.floor(item.duration / 60)}m {item.duration % 60}s)
                                </span>
                              )}
                            </p>
                            <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                              {formatTime(item.timestamp)}
                            </span>
                          </div>
                          {item.lead?.name && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Lead: <span className="font-medium text-gray-700">{item.lead.name}</span>
                              {item.lead.phone ? ` • ${item.lead.phone}` : ''}
                            </p>
                          )}
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                          )}
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${bg} ${text} font-semibold`}>
                              {item.activityType === 'crm' ? `CRM: ${item.subType}` : item.subType}
                            </span>
                            {item.activityType === 'followup' && item.status && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                {formatLabel(item.status)}
                              </span>
                            )}
                            {item.isSystem && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">
                                auto
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Close */}
          <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100 mt-4">
            <Button variant="outline" onClick={() => setActivityRecord(null)}>
              Close
            </Button>
          </div>
        </Modal>

        {/* Login Sessions Detail Modal */}
        <Modal
          isOpen={isLoginModalOpen}
          onClose={handleCloseLoginModal}
          title={loginRecord ? `${loginRecord.name} - Login Sessions` : 'Login Sessions'}
          size="lg"
        >
          <div className="space-y-4">
            {/* Summary */}
            {loginRecord && (
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-primary-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-primary-600">Total Login Time</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary-700">
                    {formatSeconds(loginRecord.totalSeconds)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Login Sessions</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {loginRecord.loginSessions}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Avg Session</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {formatSeconds(loginRecord.avgSeconds)}
                  </p>
                </div>
              </div>
            )}

            {/* Sessions List */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-3">
                Login / Logout Activity
              </h4>
              {loginSessionsLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : (loginSessions || []).length === 0 ? (
                <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
                  No login sessions for the selected period
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3 max-h-[350px] overflow-y-auto">
                  {loginSessions.map((session, index) => {
                    const isOnline = session.is_active;
                    const duration = isOnline
                      ? formatSeconds(Math.floor((Date.now() - new Date(session.login_at).getTime()) / 1000))
                      : formatSeconds(session.duration_seconds || 0);

                    return (
                      <div
                        key={session._id || index}
                        className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 ${
                          isOnline
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                          <span className="font-medium text-gray-900 text-sm">Session {index + 1}</span>
                          <Badge variant={isOnline ? 'success' : 'default'} size="sm">
                            {isOnline ? 'Online' : 'Ended'}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                          {/* Login */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Login</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">
                              {formatDateTime(session.login_at)}
                            </p>
                          </div>

                          {/* Arrow */}
                          <HiOutlineArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />

                          {/* Logout */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Logout</p>
                            <p className={`text-sm sm:text-base font-semibold ${isOnline ? 'text-green-600' : 'text-gray-900'}`}>
                              {isOnline ? 'Still Online' : formatDateTime(session.logout_at)}
                            </p>
                          </div>

                          {/* Duration */}
                          <div className="flex-1 text-right min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Duration</p>
                            <p className="text-sm sm:text-base font-semibold text-primary-600">
                              {duration}
                            </p>
                          </div>
                        </div>

                        {/* Device info */}
                        {(session.device || session.ip_address) && (
                          <div className="flex items-center gap-3 mt-2 sm:mt-3 pt-2 border-t border-gray-100 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <HiOutlineDesktopComputer className="w-3.5 h-3.5 flex-shrink-0" />
                              {session.device || 'Unknown device'}
                              {session.device_model ? ` • ${session.device_model}` : ''}
                            </span>
                            {session.ip_address && <span>IP: {session.ip_address}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={handleCloseLoginModal}>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default Attendance;